import { useState, useEffect } from 'react';
import { createClient } from '@supabase/supabase-js';
import Card from '../../components/base/Card';

const supabase = createClient(
  import.meta.env.VITE_PUBLIC_SUPABASE_URL,
  import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY
);

interface Order {
  id: string;
  order_number: string;
  customer_name: string;
  customer_email: string;
  amount: number;
  currency: string;
  usdt_amount: number;
  wallet_address: string;
  referral_code: string | null;
  fee: number;
  fee_percentage: string;
  total: number;
  payment_method: string;
  status: string;
  flag: string;
  created_at: string;
  updated_at: string;
}

interface Message {
  id: string;
  order_id: string;
  sender_type: 'user' | 'admin';
  message: string;
  created_at: string;
  is_read: boolean;
}

interface Contact {
  id: string;
  customer_name: string;
  customer_email: string;
  subject: string;
  message: string;
  status: string;
  created_at: string;
  updated_at: string;
}

interface ContactMessage {
  id: string;
  contact_id: string;
  sender_type: 'user' | 'admin';
  message: string;
  created_at: string;
  is_read: boolean;
}

interface Agent {
  id: string;
  agent_name: string;
  agent_email: string;
  agent_password: string;
  fee_percentage: number;
  created_at: string;
  updated_at: string;
}

export default function AdminPage() {
  const [viewType, setViewType] = useState<'orders' | 'contacts' | 'agents'>('orders');
  const [orders, setOrders] = useState<Order[]>([]);
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [agents, setAgents] = useState<Agent[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [selectedContact, setSelectedContact] = useState<Contact | null>(null);
  const [selectedAgent, setSelectedAgent] = useState<Agent | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [userEmail, setUserEmail] = useState('');
  const [messages, setMessages] = useState<Message[]>([]);
  const [contactMessages, setContactMessages] = useState<ContactMessage[]>([]);
  const [newAdminMessage, setNewAdminMessage] = useState('');
  const [activeTab, setActiveTab] = useState<'details' | 'messages'>('details');
  const [unreadCounts, setUnreadCounts] = useState<Record<string, number>>({});
  const [contactUnreadCounts, setContactUnreadCounts] = useState<Record<string, number>>({});
  const [showAgentModal, setShowAgentModal] = useState(false);
  const [agentFormData, setAgentFormData] = useState({
    agent_name: '',
    agent_email: '',
    agent_password: '',
    fee_percentage: 0
  });
  const [currentPage, setCurrentPage] = useState(1);
  const PAGE_SIZE = 100;

  const formatNumber = (num: number) => {
    return num.toLocaleString('ja-JP', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'deposited': return 'bg-blue-100 text-blue-800';
      case 'sent': return 'bg-green-100 text-green-800';
      case 'cancelled': return 'bg-red-100 text-red-800';
      case 'open': return 'bg-blue-100 text-blue-800';
      case 'closed': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'pending': return '保留中';
      case 'deposited': return '入金';
      case 'sent': return '送金';
      case 'cancelled': return 'キャンセル';
      case 'open': return '対応中';
      case 'closed': return '完了';
      default: return status;
    }
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleString('ja-JP');
  };

  const stats = {
    totalOrders: viewType === 'contacts' ? contacts.length : orders.length,
    pendingOrders: viewType === 'contacts'
      ? contacts.filter(c => c.status === 'open').length
      : orders.filter(o => o.status === 'pending').length,
    depositedOrders: orders.filter(o => o.status === 'deposited').length,
    sentOrders: viewType === 'contacts'
      ? contacts.filter(c => c.status === 'closed').length
      : orders.filter(o => o.status === 'sent').length,
    cancelledOrders: orders.filter(o => o.status === 'cancelled').length,
    totalVolume: orders.reduce((sum, o) => sum + Number(o.usdt_amount || 0), 0),
    unreadMessages: viewType === 'contacts'
      ? Object.values(contactUnreadCounts).reduce((a, b) => a + b, 0)
      : Object.values(unreadCounts).reduce((a, b) => a + b, 0),
  };

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    
    if (!session) {
      window.REACT_APP_NAVIGATE('/login');
      return;
    }

    setUserEmail(session.user.email || '');
    fetchOrders();
    fetchContacts();
    fetchAgents();
    fetchUnreadCounts();
    fetchContactUnreadCounts();
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    window.REACT_APP_NAVIGATE('/login');
  };

  const fetchOrders = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('orders')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) {
        console.warn('Could not fetch orders:', error.message);
        setOrders([]);
        return;
      }
      setOrders(data || []);
    } catch (error) {
      console.warn('Could not fetch orders:', error);
      setOrders([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchContacts = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('contacts')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) {
        console.warn('Could not fetch contacts:', error.message);
        setContacts([]);
        return;
      }
      setContacts(data || []);
    } catch (error) {
      console.warn('Could not fetch contacts:', error);
      setContacts([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchAgents = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('agents')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) {
        console.warn('Could not fetch agents:', error.message);
        setAgents([]);
        return;
      }
      setAgents(data || []);
    } catch (error) {
      console.warn('Could not fetch agents:', error);
      setAgents([]);
    } finally {
      setLoading(false);
    }
  };

  // Fetch unread message counts for all orders
  const fetchUnreadCounts = async () => {
    try {
      const { data, error } = await supabase
        .from('messages')
        .select('order_id')
        .eq('sender_type', 'user')
        .eq('is_read', false);

      if (error) {
        console.warn('Could not fetch unread counts:', error.message);
        return;
      }

      // Count unread messages per order
      const counts: Record<string, number> = {};
      data?.forEach((msg) => {
        counts[msg.order_id] = (counts[msg.order_id] || 0) + 1;
      });

      setUnreadCounts(counts);
    } catch (error) {
      console.warn('Could not fetch unread counts:', error);
    }
  };

  // Fetch unread message counts for all contacts
  const fetchContactUnreadCounts = async () => {
    try {
      const { data, error } = await supabase
        .from('contact_messages')
        .select('contact_id')
        .eq('sender_type', 'user')
        .eq('is_read', false);

      if (error) {
        console.warn('Could not fetch contact unread counts:', error.message);
        return;
      }

      // Count unread messages per contact
      const counts: Record<string, number> = {};
      data?.forEach((msg) => {
        counts[msg.contact_id] = (counts[msg.contact_id] || 0) + 1;
      });

      setContactUnreadCounts(counts);
    } catch (error) {
      console.warn('Could not fetch contact unread counts:', error);
    }
  };

  const updateOrderStatus = async (orderId: string, newStatus: string) => {
    try {
      const { error } = await supabase
        .from('orders')
        .update({ status: newStatus, updated_at: new Date().toISOString() })
        .eq('id', orderId);

      if (error) throw error;
      
      await fetchOrders();
      if (selectedOrder?.id === orderId) {
        setSelectedOrder({ ...selectedOrder, status: newStatus });
      }
    } catch (error) {
      console.warn('Error updating order status:', error);
      alert('ステータスの更新に失敗しました');
    }
  };

  const updateContactStatus = async (contactId: string, newStatus: string) => {
    try {
      const { error } = await supabase
        .from('contacts')
        .update({ status: newStatus, updated_at: new Date().toISOString() })
        .eq('id', contactId);

      if (error) throw error;
      
      await fetchContacts();
      if (selectedContact?.id === contactId) {
        setSelectedContact({ ...selectedContact, status: newStatus });
      }
    } catch (error) {
      console.warn('Error updating contact status:', error);
      alert('ステータスの更新に失敗しました');
    }
  };

  // Fetch messages for selected order
  const fetchOrderMessages = async (orderId: string) => {
    try {
      const { data, error } = await supabase
        .from('messages')
        .select('*')
        .eq('order_id', orderId)
        .order('created_at', { ascending: true });

      if (error) throw error;
      setMessages(data || []);

      // Mark user messages as read
      const { error: updateError } = await supabase
        .from('messages')
        .update({ is_read: true })
        .eq('order_id', orderId)
        .eq('sender_type', 'user')
        .eq('is_read', false);

      if (updateError) {
        console.warn('Error marking messages as read:', updateError);
      } else {
        // Refresh unread counts after marking as read
        await fetchUnreadCounts();
      }
    } catch (error) {
      console.warn('Error fetching messages:', error);
    }
  };

  // Fetch messages for selected contact
  const fetchContactMessages = async (contactId: string) => {
    try {
      const { data, error } = await supabase
        .from('contact_messages')
        .select('*')
        .eq('contact_id', contactId)
        .order('created_at', { ascending: true });

      if (error) throw error;
      setContactMessages(data || []);

      // Mark user messages as read
      const { error: updateError } = await supabase
        .from('contact_messages')
        .update({ is_read: true })
        .eq('contact_id', contactId)
        .eq('sender_type', 'user')
        .eq('is_read', false);

      if (updateError) {
        console.warn('Error marking contact messages as read:', updateError);
      } else {
        // Refresh unread counts after marking as read
        await fetchContactUnreadCounts();
      }
    } catch (error) {
      console.warn('Error fetching contact messages:', error);
    }
  };

  // Send admin message for order
  const sendAdminMessage = async () => {
    if (!newAdminMessage.trim() || !selectedOrder) return;

    try {
      const { error } = await supabase
        .from('messages')
        .insert([
          {
            order_id: selectedOrder.id,
            sender_type: 'admin',
            message: newAdminMessage.trim()
          }
        ]);

      if (error) throw error;
      
      setNewAdminMessage('');
      await fetchOrderMessages(selectedOrder.id);
    } catch (error) {
      console.warn('Error sending message:', error);
      alert('Failed to send message. Please try again.');
    }
  };

  // Send admin message for contact
  const sendAdminContactMessage = async () => {
    if (!newAdminMessage.trim() || !selectedContact) return;

    try {
      const { error } = await supabase
        .from('contact_messages')
        .insert([
          {
            contact_id: selectedContact.id,
            sender_type: 'admin',
            message: newAdminMessage.trim()
          }
        ]);

      if (error) throw error;
      
      setNewAdminMessage('');
      await fetchContactMessages(selectedContact.id);
    } catch (error) {
      console.warn('Error sending contact message:', error);
      alert('Failed to send message. Please try again.');
    }
  };

  // Update handleOrderClick to mark messages as read
  const handleOrderClick = async (order: Order) => {
    setSelectedOrder(order);
    setSelectedContact(null);
    setActiveTab('details');
    await fetchOrderMessages(order.id);
  };

  // Handle contact click
  const handleContactClick = async (contact: Contact) => {
    setSelectedContact(contact);
    setSelectedOrder(null);
    setActiveTab('details');
    await fetchContactMessages(contact.id);
  };

  const filteredOrders = orders.filter(order => {
    const matchesStatus = statusFilter === 'all' || order.status === statusFilter;
    const matchesSearch = 
      order.customer_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.customer_email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.wallet_address.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesStatus && matchesSearch;
  });

  const totalPages = Math.ceil(filteredOrders.length / PAGE_SIZE);
  const paginatedOrders = filteredOrders.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const filteredContacts = contacts.filter(contact => {
    const matchesStatus = statusFilter === 'all' || contact.status === statusFilter;
    const matchesSearch = 
      contact.customer_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      contact.customer_email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      contact.subject.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesStatus && matchesSearch;
  });

  const filteredAgents = agents.filter(agent => {
    const matchesSearch = 
      agent.agent_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      agent.agent_email.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesSearch;
  });

  const handleAddAgent = async () => {
    if (!agentFormData.agent_name || !agentFormData.agent_email || !agentFormData.agent_password) {
      alert('すべての項目を入力してください');
      return;
    }

    try {
      const { error } = await supabase
        .from('agents')
        .insert([agentFormData]);

      if (error) throw error;
      
      await fetchAgents();
      setShowAgentModal(false);
      setAgentFormData({ agent_name: '', agent_email: '', agent_password: '', fee_percentage: 0 });
      setSelectedAgent(null);
    } catch (error) {
      console.warn('Error adding agent:', error);
      alert('代理店の追加に失敗しました');
    }
  };

  const handleUpdateAgent = async () => {
    if (!selectedAgent || !agentFormData.agent_name || !agentFormData.agent_email || !agentFormData.agent_password) {
      alert('すべての項目を入力してください');
      return;
    }

    try {
      const { error } = await supabase
        .from('agents')
        .update({
          agent_name: agentFormData.agent_name,
          agent_email: agentFormData.agent_email,
          agent_password: agentFormData.agent_password,
          fee_percentage: agentFormData.fee_percentage,
          updated_at: new Date().toISOString()
        })
        .eq('id', selectedAgent.id);

      if (error) throw error;
      
      await fetchAgents();
      setShowAgentModal(false);
      setAgentFormData({ agent_name: '', agent_email: '', agent_password: '', fee_percentage: 0 });
      setSelectedAgent(null);
    } catch (error) {
      console.warn('Error updating agent:', error);
      alert('代理店の更新に失敗しました');
    }
  };

  const handleDeleteAgent = async (agentId: string) => {
    if (!confirm('この代理店を削除してもよろしいですか？')) return;

    try {
      const { error } = await supabase
        .from('agents')
        .delete()
        .eq('id', agentId);

      if (error) throw error;
      
      await fetchAgents();
    } catch (error) {
      console.warn('Error deleting agent:', error);
      alert('代理店の削除に失敗しました');
    }
  };

  const openAgentModal = (agent?: Agent) => {
    if (agent) {
      setSelectedAgent(agent);
      setAgentFormData({
        agent_name: agent.agent_name,
        agent_email: agent.agent_email,
        agent_password: agent.agent_password,
        fee_percentage: agent.fee_percentage || 0
      });
    } else {
      setSelectedAgent(null);
      setAgentFormData({ agent_name: '', agent_email: '', agent_password: '', fee_percentage: 0 });
    }
    setShowAgentModal(true);
  };

  const updateOrderFlag = async (orderId: string, newFlag: string, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      const { error } = await supabase
        .from('orders')
        .update({ flag: newFlag, updated_at: new Date().toISOString() })
        .eq('id', orderId);

      if (error) throw error;

      setOrders(prev => prev.map(o => o.id === orderId ? { ...o, flag: newFlag } : o));
      if (selectedOrder?.id === orderId) {
        setSelectedOrder(prev => prev ? { ...prev, flag: newFlag } : prev);
      }
    } catch (error) {
      console.warn('Error updating flag:', error);
    }
  };

  const getFlagConfig = (flag: string) => {
    switch (flag) {
      case 'needs_reply': return { label: '要返信', color: 'bg-red-100 text-red-700 border-red-300', icon: 'ri-flag-fill text-red-500' };
      case 'confirmed': return { label: '確認', color: 'bg-green-100 text-green-700 border-green-300', icon: 'ri-checkbox-circle-fill text-green-500' };
      default: return { label: 'なし', color: 'bg-gray-100 text-gray-500 border-gray-200', icon: 'ri-flag-line text-gray-400' };
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">管理画面</h1>
              <p className="mt-2 text-gray-600">注文とお問い合わせの管理</p>
            </div>
            <div className="flex items-center gap-4">
              <div className="text-right">
                <p className="text-sm text-gray-500">ログイン中</p>
                <p className="text-sm font-medium text-gray-900">{userEmail}</p>
              </div>
              <button
                onClick={handleLogout}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-medium flex items-center gap-2 whitespace-nowrap"
              >
                <i className="ri-logout-box-line text-lg"></i>
                <span>ログアウト</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* View Type Tabs */}
        <div className="flex space-x-4 mb-8">
          <button
            onClick={() => {
              setViewType('orders');
              setSelectedOrder(null);
              setSelectedContact(null);
              setSelectedAgent(null);
              setStatusFilter('all');
              setSearchTerm('');
            }}
            className={`px-6 py-3 rounded-lg font-medium transition-colors whitespace-nowrap ${
              viewType === 'orders'
                ? 'bg-teal-600 text-white'
                : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-200'
            }`}
          >
            <i className="ri-shopping-cart-line mr-2"></i>
            注文管理
          </button>
          <button
            onClick={() => {
              setViewType('contacts');
              setSelectedOrder(null);
              setSelectedContact(null);
              setSelectedAgent(null);
              setStatusFilter('all');
              setSearchTerm('');
            }}
            className={`px-6 py-3 rounded-lg font-medium transition-colors whitespace-nowrap relative ${
              viewType === 'contacts'
                ? 'bg-teal-600 text-white'
                : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-200'
            }`}
          >
            <i className="ri-customer-service-line mr-2"></i>
            お問い合わせ管理
            {stats.unreadMessages > 0 && (
              <span className="absolute -top-2 -right-2 flex h-6 w-6 items-center justify-center">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-orange-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-6 w-6 bg-orange-500 items-center justify-center text-white text-xs font-bold">
                  {stats.unreadMessages}
                </span>
              </span>
            )}
          </button>
          <button
            onClick={() => {
              setViewType('agents');
              setSelectedOrder(null);
              setSelectedContact(null);
              setSelectedAgent(null);
              setStatusFilter('all');
              setSearchTerm('');
            }}
            className={`px-6 py-3 rounded-lg font-medium transition-colors whitespace-nowrap ${
              viewType === 'agents'
                ? 'bg-teal-600 text-white'
                : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-200'
            }`}
          >
            <i className="ri-team-line mr-2"></i>
            代理店管理
          </button>
        </div>

        {/* Stats - Orders */}
        {viewType === 'orders' && (
          <div className="grid grid-cols-1 md:grid-cols-7 gap-6 mb-8">
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">総注文数</p>
                  <p className="text-2xl font-bold text-gray-900 mt-1">{stats.totalOrders.toLocaleString()}</p>
                </div>
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                  <i className="ri-shopping-cart-line text-2xl text-blue-600"></i>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">保留中</p>
                  <p className="text-2xl font-bold text-yellow-600 mt-1">{stats.pendingOrders.toLocaleString()}</p>
                </div>
                <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center">
                  <i className="ri-time-line text-2xl text-yellow-600"></i>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">入金</p>
                  <p className="text-2xl font-bold text-blue-600 mt-1">{stats.depositedOrders.toLocaleString()}</p>
                </div>
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                  <i className="ri-bank-line text-2xl text-blue-600"></i>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">送金</p>
                  <p className="text-2xl font-bold text-green-600 mt-1">{stats.sentOrders.toLocaleString()}</p>
                </div>
                <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                  <i className="ri-send-plane-line text-2xl text-green-600"></i>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">キャンセル</p>
                  <p className="text-2xl font-bold text-red-600 mt-1">{stats.cancelledOrders.toLocaleString()}</p>
                </div>
                <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center">
                  <i className="ri-close-line text-2xl text-red-600"></i>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow p-6 relative">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">総取引額</p>
                  <p className="text-2xl font-bold text-teal-600 mt-1">{formatNumber(stats.totalVolume)}</p>
                  <p className="text-xs text-gray-500 mt-1">USDT</p>
                </div>
                <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center">
                  <i className="ri-money-dollar-circle-line text-2xl text-teal-600"></i>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow p-6 relative">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">未読メッセージ</p>
                  <p className="text-2xl font-bold text-orange-600 mt-1">{stats.unreadMessages.toLocaleString()}</p>
                </div>
                <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
                  <i className="ri-message-3-line text-2xl text-orange-600"></i>
                </div>
              </div>
              {stats.unreadMessages > 0 && (
                <div className="absolute top-2 right-2">
                  <span className="flex h-3 w-3">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-orange-400 opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-3 w-3 bg-orange-500"></span>
                  </span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Stats - Contacts */}
        {viewType === 'contacts' && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">総お問い合わせ数</p>
                  <p className="text-2xl font-bold text-gray-900 mt-1">{stats.totalOrders.toLocaleString()}</p>
                </div>
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                  <i className="ri-customer-service-line text-2xl text-blue-600"></i>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">対応中</p>
                  <p className="text-2xl font-bold text-blue-600 mt-1">{stats.pendingOrders.toLocaleString()}</p>
                </div>
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                  <i className="ri-time-line text-2xl text-blue-600"></i>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">完了</p>
                  <p className="text-2xl font-bold text-green-600 mt-1">{stats.sentOrders.toLocaleString()}</p>
                </div>
                <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                  <i className="ri-check-line text-2xl text-green-600"></i>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow p-6 relative">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">未読メッセージ</p>
                  <p className="text-2xl font-bold text-orange-600 mt-1">{stats.unreadMessages.toLocaleString()}</p>
                </div>
                <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
                  <i className="ri-message-3-line text-2xl text-orange-600"></i>
                </div>
              </div>
              {stats.unreadMessages > 0 && (
                <div className="absolute top-2 right-2">
                  <span className="flex h-3 w-3">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-orange-400 opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-3 w-3 bg-orange-500"></span>
                  </span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Stats - Agents */}
        {viewType === 'agents' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">総代理店数</p>
                  <p className="text-2xl font-bold text-gray-900 mt-1">{agents.length.toLocaleString()}</p>
                </div>
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                  <i className="ri-team-line text-2xl text-blue-600"></i>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">新規追加</p>
                  <button
                    onClick={() => openAgentModal()}
                    className="mt-2 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors font-medium whitespace-nowrap"
                  >
                    <i className="ri-add-line mr-2"></i>
                    代理店を追加
                  </button>
                </div>
                <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center">
                  <i className="ri-add-circle-line text-2xl text-teal-600"></i>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Filters */}
        {(viewType === 'orders' || viewType === 'contacts') && (
          <div className="bg-white rounded-lg shadow p-6 mb-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">検索</label>
                <div className="relative">
                  <input
                    type="text"
                    placeholder={viewType === 'orders' ? '名前、メール、ウォレットアドレスで検索...' : '名前、メール、件名で検索...'}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                  />
                  <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-lg"></i>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">ステータス</label>
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="w-full px-4 py-2 pr-8 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                >
                  <option value="all">すべて</option>
                  {viewType === 'orders' ? (
                    <>
                      <option value="pending">保留中</option>
                      <option value="deposited">入金</option>
                      <option value="sent">送金</option>
                      <option value="cancelled">キャンセル</option>
                    </>
                  ) : (
                    <>
                      <option value="open">対応中</option>
                      <option value="closed">完了</option>
                    </>
                  )}
                </select>
              </div>
            </div>
          </div>
        )}

        {/* Agents Search */}
        {viewType === 'agents' && (
          <div className="bg-white rounded-lg shadow p-6 mb-8">
            <div className="grid grid-cols-1 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">検索</label>
                <div className="relative">
                  <input
                    type="text"
                    placeholder="代理店名、メールアドレスで検索..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                  />
                  <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-lg"></i>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Orders Table */}
        {viewType === 'orders' && (
          <div className="bg-white rounded-lg shadow overflow-hidden">
            {loading ? (
              <div className="flex items-center justify-center py-20">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
              </div>
            ) : filteredOrders.length === 0 ? (
              <div className="text-center py-20">
                <i className="ri-inbox-line text-6xl text-gray-300 mb-4"></i>
                <p className="text-gray-500">注文がありません</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                {/* Pagination Top */}
                {totalPages > 1 && (
                  <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-gray-50">
                    <p className="text-sm text-gray-600">
                      全 <span className="font-semibold">{filteredOrders.length}</span> 件中{' '}
                      <span className="font-semibold">{(currentPage - 1) * PAGE_SIZE + 1}</span> -{' '}
                      <span className="font-semibold">{Math.min(currentPage * PAGE_SIZE, filteredOrders.length)}</span> 件を表示
                    </p>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                        disabled={currentPage === 1}
                        className="px-3 py-1.5 rounded-lg border border-gray-300 text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors whitespace-nowrap"
                      >
                        <i className="ri-arrow-left-s-line"></i> 前へ
                      </button>
                      {Array.from({ length: totalPages }, (_, i) => i + 1)
                        .filter(p => p === 1 || p === totalPages || Math.abs(p - currentPage) <= 2)
                        .reduce<(number | '...')[]>((acc, p, idx, arr) => {
                          if (idx > 0 && typeof arr[idx - 1] === 'number' && (p as number) - (arr[idx - 1] as number) > 1) {
                            acc.push('...');
                          }
                          acc.push(p);
                          return acc;
                        }, [])
                        .map((item, idx) =>
                          item === '...' ? (
                            <span key={`ellipsis-top-${idx}`} className="px-2 text-gray-400 text-sm">…</span>
                          ) : (
                            <button
                              key={`top-${item}`}
                              onClick={() => setCurrentPage(item as number)}
                              className={`px-3 py-1.5 rounded-lg border text-sm font-medium transition-colors whitespace-nowrap ${
                                currentPage === item
                                  ? 'bg-teal-600 text-white border-teal-600'
                                  : 'border-gray-300 text-gray-700 bg-white hover:bg-gray-50'
                              }`}
                            >
                              {item}
                            </button>
                          )
                        )}
                      <button
                        onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                        disabled={currentPage === totalPages}
                        className="px-3 py-1.5 rounded-lg border border-gray-300 text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors whitespace-nowrap"
                      >
                        次へ <i className="ri-arrow-right-s-line"></i>
                      </button>
                    </div>
                  </div>
                )}
                <table className="w-full">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">注文日時</th>
                      <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">注文番号</th>
                      <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">お客様情報</th>
                      <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">金額</th>
                      <th className="px-3 py-3 text-left text-xs font-medium text-teal-600 uppercase tracking-wider whitespace-nowrap">USDT</th>
                      <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">紹介コード</th>
                      <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">ステータス</th>
                      <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">メッセージ</th>
                      <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">フラグ</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {paginatedOrders.map((order) => (
                      <tr key={order.id} className="hover:bg-gray-50 transition-colors cursor-pointer" onClick={() => handleOrderClick(order)}>
                        <td className="px-3 py-3 whitespace-nowrap text-xs text-gray-900">
                          {new Date(order.created_at).toLocaleString('ja-JP')}
                        </td>
                        <td className="px-3 py-3 whitespace-nowrap text-xs font-medium text-gray-900">
                          {order.order_number || '-'}
                        </td>
                        <td className="px-3 py-3 whitespace-nowrap">
                          <div className="text-xs font-medium text-gray-900">{order.customer_name}</div>
                          <div className="text-xs text-gray-500">{order.customer_email}</div>
                        </td>
                        <td className="px-3 py-3 whitespace-nowrap text-xs text-gray-900">
                          {Number(order.amount).toLocaleString()} {order.currency}
                        </td>
                        <td className="px-3 py-3 whitespace-nowrap text-xs font-medium text-teal-600">
                          {formatNumber(Number(order.usdt_amount))} USDT
                        </td>
                        <td className="px-3 py-3 whitespace-nowrap text-xs text-gray-900">
                          {order.referral_code || '-'}
                        </td>
                        <td className="px-3 py-3 whitespace-nowrap">
                          <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(order.status)}`}>
                            {getStatusText(order.status)}
                          </span>
                        </td>
                        <td className="px-3 py-3 whitespace-nowrap">
                          {unreadCounts[order.id] > 0 ? (
                            <div className="flex items-center gap-1">
                              <span className="relative flex h-3 w-3">
                                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-orange-400 opacity-75"></span>
                                <span className="relative inline-flex rounded-full h-3 w-3 bg-orange-500"></span>
                              </span>
                              <span className="text-xs font-semibold text-orange-600">
                                {unreadCounts[order.id]}件
                              </span>
                            </div>
                          ) : (
                            <span className="text-xs text-gray-400">-</span>
                          )}
                        </td>
                        <td className="px-3 py-3 whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                          <div className="flex items-center gap-1">
                            {(['none', 'needs_reply', 'confirmed'] as const).map((flagVal) => {
                              const cfg = getFlagConfig(flagVal);
                              const isActive = (order.flag || 'none') === flagVal;
                              return (
                                <button
                                  key={flagVal}
                                  onClick={(e) => updateOrderFlag(order.id, flagVal, e)}
                                  title={cfg.label}
                                  className={`px-2 py-1 rounded border text-xs font-medium transition-all whitespace-nowrap cursor-pointer ${
                                    isActive
                                      ? cfg.color + ' font-bold shadow-sm'
                                      : 'bg-white text-gray-400 border-gray-200 hover:border-gray-400'
                                  }`}
                                >
                                  {cfg.label}
                                </button>
                              );
                            })}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 bg-gray-50">
                    <p className="text-sm text-gray-600">
                      全 <span className="font-semibold">{filteredOrders.length}</span> 件中{' '}
                      <span className="font-semibold">{(currentPage - 1) * PAGE_SIZE + 1}</span> -{' '}
                      <span className="font-semibold">{Math.min(currentPage * PAGE_SIZE, filteredOrders.length)}</span> 件を表示
                    </p>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                        disabled={currentPage === 1}
                        className="px-3 py-1.5 rounded-lg border border-gray-300 text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors whitespace-nowrap"
                      >
                        <i className="ri-arrow-left-s-line"></i> 前へ
                      </button>
                      {Array.from({ length: totalPages }, (_, i) => i + 1)
                        .filter(p => p === 1 || p === totalPages || Math.abs(p - currentPage) <= 2)
                        .reduce<(number | '...')[]>((acc, p, idx, arr) => {
                          if (idx > 0 && typeof arr[idx - 1] === 'number' && (p as number) - (arr[idx - 1] as number) > 1) {
                            acc.push('...');
                          }
                          acc.push(p);
                          return acc;
                        }, [])
                        .map((item, idx) =>
                          item === '...' ? (
                            <span key={`ellipsis-bottom-${idx}`} className="px-2 text-gray-400 text-sm">…</span>
                          ) : (
                            <button
                              key={`bottom-${item}`}
                              onClick={() => setCurrentPage(item as number)}
                              className={`px-3 py-1.5 rounded-lg border text-sm font-medium transition-colors whitespace-nowrap ${
                                currentPage === item
                                  ? 'bg-teal-600 text-white border-teal-600'
                                  : 'border-gray-300 text-gray-700 bg-white hover:bg-gray-50'
                              }`}
                            >
                              {item}
                            </button>
                          )
                        )}
                      <button
                        onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                        disabled={currentPage === totalPages}
                        className="px-3 py-1.5 rounded-lg border border-gray-300 text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors whitespace-nowrap"
                      >
                        次へ <i className="ri-arrow-right-s-line"></i>
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Contacts Table */}
        {viewType === 'contacts' && (
          <div className="bg-white rounded-lg shadow overflow-hidden">
            {loading ? (
              <div className="flex items-center justify-center py-20">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
              </div>
            ) : filteredContacts.length === 0 ? (
              <div className="text-center py-20">
                <i className="ri-inbox-line text-6xl text-gray-300 mb-4"></i>
                <p className="text-gray-500">お問い合わせがありません</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">受信日時</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">お客様情報</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">件名</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">メッセージ</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">ステータス</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">未読</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">操作</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredContacts.map((contact) => (
                      <tr key={contact.id} className="hover:bg-gray-50 transition-colors cursor-pointer" onClick={() => handleContactClick(contact)}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {new Date(contact.created_at).toLocaleString('ja-JP')}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">{contact.customer_name}</div>
                          <div className="text-sm text-gray-500">{contact.customer_email}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {contact.subject}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900 max-w-xs truncate">
                          {contact.message}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(contact.status)}`}>
                            {getStatusText(contact.status)}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {contactUnreadCounts[contact.id] > 0 ? (
                            <div className="flex items-center gap-2">
                              <span className="relative flex h-3 w-3">
                                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-orange-400 opacity-75"></span>
                                <span className="relative inline-flex rounded-full h-3 w-3 bg-orange-500"></span>
                              </span>
                              <span className="text-sm font-semibold text-orange-600">
                                {contactUnreadCounts[contact.id]}件
                              </span>
                            </div>
                          ) : (
                            <span className="text-sm text-gray-400">-</span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleContactClick(contact);
                            }}
                            className="text-teal-600 hover:text-teal-900 font-medium whitespace-nowrap"
                          >
                            詳細を見る
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Agents Table */}
        {viewType === 'agents' && (
          <div className="bg-white rounded-lg shadow overflow-hidden">
            {loading ? (
              <div className="flex items-center justify-center py-20">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
              </div>
            ) : filteredAgents.length === 0 ? (
              <div className="text-center py-20">
                <i className="ri-inbox-line text-6xl text-gray-300 mb-4"></i>
                <p className="text-gray-500">代理店がありません</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">登録日時</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">代理店名</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">メールアドレス</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">パスワード</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">手数料</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">操作</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredAgents.map((agent) => (
                      <tr key={agent.id} className="hover:bg-gray-50 transition-colors">
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {new Date(agent.created_at).toLocaleString('ja-JP')}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {agent.agent_name}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {agent.agent_email}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-mono">
                          {agent.agent_password}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {agent.fee_percentage}%
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm space-x-2">
                          <button
                            onClick={() => openAgentModal(agent)}
                            className="text-teal-600 hover:text-teal-900 font-medium whitespace-nowrap"
                          >
                            編集
                          </button>
                          <button
                            onClick={() => handleDeleteAgent(agent.id)}
                            className="text-red-600 hover:text-red-900 font-medium whitespace-nowrap"
                          >
                            削除
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Order Detail Modal */}
      {selectedOrder && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50" onClick={() => setSelectedOrder(null)}>
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
              <h2 className="text-xl font-bold text-gray-900">注文詳細</h2>
              <button
                onClick={() => setSelectedOrder(null)}
                className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 transition-colors"
              >
                <i className="ri-close-line text-2xl"></i>
              </button>
            </div>

            <div className="p-6">
              {/* Tabs */}
              <div className="flex space-x-4 mb-6">
                <button
                  onClick={() => setActiveTab('details')}
                  className={`px-4 py-2 rounded-lg font-medium transition-colors whitespace-nowrap ${
                    activeTab === 'details'
                      ? 'bg-teal-600 text-white'
                      : 'bg-white border border-gray-200 text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  注文詳細
                </button>
                <button
                  onClick={() => setActiveTab('messages')}
                  className={`px-4 py-2 rounded-lg font-medium transition-colors whitespace-nowrap relative ${
                    activeTab === 'messages'
                      ? 'bg-teal-600 text-white'
                      : 'bg-white border border-gray-200 text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  メッセージ ({messages.length})
                  {unreadCounts[selectedOrder.id] > 0 && (
                    <span className="absolute -top-1 -right-1 flex h-5 w-5 items-center justify-center">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-orange-400 opacity-75"></span>
                      <span className="relative inline-flex rounded-full h-5 w-5 bg-orange-500 items-center justify-center text-white text-xs font-bold">
                        {unreadCounts[selectedOrder.id]}
                      </span>
                    </span>
                  )}
                </button>
              </div>

              {/* Details Tab */}
              {activeTab === 'details' && (
                <>
                  {/* Status Update */}
                  <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                    <label className="block text-sm font-medium text-gray-700 mb-2">ステータス更新</label>
                    <div className="grid grid-cols-2 gap-2">
                      <button
                        onClick={() => updateOrderStatus(selectedOrder.id, 'pending')}
                        className={`px-4 py-2 rounded-lg font-medium transition-colors whitespace-nowrap ${
                          selectedOrder.status === 'pending'
                            ? 'bg-yellow-600 text-white'
                            : 'bg-white border border-yellow-600 text-yellow-600 hover:bg-yellow-50'
                        }`}
                      >
                        保留中
                      </button>
                      <button
                        onClick={() => updateOrderStatus(selectedOrder.id, 'deposited')}
                        className={`px-4 py-2 rounded-lg font-medium transition-colors whitespace-nowrap ${
                          selectedOrder.status === 'deposited'
                            ? 'bg-blue-600 text-white'
                            : 'bg-white border border-blue-600 text-blue-600 hover:bg-blue-50'
                        }`}
                      >
                        入金
                      </button>
                      <button
                        onClick={() => updateOrderStatus(selectedOrder.id, 'sent')}
                        className={`px-4 py-2 rounded-lg font-medium transition-colors whitespace-nowrap ${
                          selectedOrder.status === 'sent'
                            ? 'bg-green-600 text-white'
                            : 'bg-white border border-green-600 text-green-600 hover:bg-green-50'
                        }`}
                      >
                        送金
                      </button>
                      <button
                        onClick={() => updateOrderStatus(selectedOrder.id, 'cancelled')}
                        className={`px-4 py-2 rounded-lg font-medium transition-colors whitespace-nowrap ${
                          selectedOrder.status === 'cancelled'
                            ? 'bg-red-600 text-white'
                            : 'bg-white border border-red-600 text-red-600 hover:bg-red-50'
                        }`}
                      >
                        キャンセル
                      </button>
                    </div>
                  </div>

                  {/* Order Information */}
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-sm text-gray-500">注文ID</p>
                        <p className="text-sm font-medium text-gray-900 mt-1 break-all">{selectedOrder.order_number}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">注文日時</p>
                        <p className="text-sm font-medium text-gray-900 mt-1">
                          {new Date(selectedOrder.created_at).toLocaleString('ja-JP')}
                        </p>
                      </div>
                    </div>

                    <div className="border-t border-gray-200 pt-4">
                      <h3 className="text-lg font-semibold text-gray-900 mb-3">お客様情報</h3>
                      <div className="space-y-3">
                        <div>
                          <p className="text-sm text-gray-500">お名前</p>
                          <p className="text-sm font-medium text-gray-900 mt-1">{selectedOrder.customer_name}</p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-500">メールアドレス</p>
                          <p className="text-sm font-medium text-gray-900 mt-1">{selectedOrder.customer_email}</p>
                        </div>
                      </div>
                    </div>

                    <div className="border-t border-gray-200 pt-4">
                      <h3 className="text-lg font-semibold text-gray-900 mb-3">取引情報</h3>
                      <div className="space-y-3">
                        <div className="flex justify-between">
                          <p className="text-sm text-gray-500">金額</p>
                          <p className="text-sm font-medium text-gray-900">{Number(selectedOrder.amount).toLocaleString()} {selectedOrder.currency}</p>
                        </div>
                        <div className="flex justify-between">
                          <p className="text-sm text-gray-500">USDT換算</p>
                          <p className="text-sm font-medium text-gray-900">{formatNumber(Number(selectedOrder.usdt_amount))} USDT</p>
                        </div>
                        <div className="flex justify-between">
                          <p className="text-sm text-gray-500">ウォレットアドレス</p>
                          <p className="text-sm font-medium text-gray-900 font-mono break-all">{selectedOrder.wallet_address}</p>
                        </div>
                        {selectedOrder.referral_code && (
                          <div className="flex justify-between">
                            <p className="text-sm text-gray-500">紹介コード</p>
                            <p className="text-sm font-medium text-gray-900">{selectedOrder.referral_code}</p>
                          </div>
                        )}
                        <div className="flex justify-between">
                          <p className="text-sm text-gray-500">支払方法</p>
                          <p className="text-sm font-medium text-gray-900">{selectedOrder.payment_method}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </>
              )}

              {/* Messages Tab */}
              {activeTab === 'messages' && (
                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">顧客メッセージ</h3>
                  
                  {/* Messages Display */}
                  <div className="max-h-64 overflow-y-auto mb-4 space-y-3">
                    {messages.length === 0 ? (
                      <div className="text-center py-8 text-gray-400">
                        <i className="ri-chat-3-line text-4xl mb-2"></i>
                        <p>この注文にはメッセージがありません</p>
                      </div>
                    ) : (
                      messages.map((message) => (
                        <div
                          key={message.id}
                          className={`flex ${message.sender_type === 'admin' ? 'justify-end' : 'justify-start'}`}
                        >
                          <div className={`max-w-md px-4 py-3 rounded-lg relative ${
                            message.sender_type === 'admin'
                              ? 'bg-teal-600 text-white'
                              : message.is_read 
                                ? 'bg-white border border-gray-200 text-gray-900'
                                : 'bg-orange-50 border-2 border-orange-200 text-gray-900'
                          }`}>
                            {message.sender_type === 'user' && !message.is_read && (
                              <div className="absolute -top-2 -left-2">
                                <span className="flex h-4 w-4">
                                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-orange-400 opacity-75"></span>
                                  <span className="relative inline-flex rounded-full h-4 w-4 bg-orange-500"></span>
                                </span>
                              </div>
                            )}
                            <p className="text-sm">{message.message}</p>
                            <p className={`text-xs mt-2 ${
                              message.sender_type === 'admin'
                                ? 'text-teal-100'
                                : message.is_read
                                  ? 'text-gray-500'
                                  : 'text-orange-600 font-semibold'
                            }`}>
                              {message.sender_type === 'admin' ? '管理者' : message.is_read ? '顧客' : '顧客（新着）'} • {formatDate(message.created_at)}
                            </p>
                          </div>
                        </div>
                      ))
                    )}
                  </div>

                  {/* Send Message */}
                  <div className="flex space-x-3">
                    <input
                      type="text"
                      value={newAdminMessage}
                      onChange={(e) => setNewAdminMessage(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && sendAdminMessage()}
                      className="flex-1 bg-white border border-gray-300 text-gray-900 rounded-lg px-4 py-3 outline-none focus:ring-2 focus:ring-teal-500 text-sm"
                      placeholder="返信を入力..."
                    />
                    <button
                      onClick={sendAdminMessage}
                      disabled={!newAdminMessage.trim()}
                      className="bg-teal-600 hover:bg-teal-700 disabled:bg-gray-300 text-white px-4 py-3 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
                    >
                      <i className="ri-send-plane-fill"></i>
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Contact Detail Modal */}
      {selectedContact && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50" onClick={() => setSelectedContact(null)}>
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
              <h2 className="text-xl font-bold text-gray-900">お問い合わせ詳細</h2>
              <button
                onClick={() => setSelectedContact(null)}
                className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 transition-colors"
              >
                <i className="ri-close-line text-2xl"></i>
              </button>
            </div>

            <div className="p-6">
              {/* Tabs */}
              <div className="flex space-x-4 mb-6">
                <button
                  onClick={() => setActiveTab('details')}
                  className={`px-4 py-2 rounded-lg font-medium transition-colors whitespace-nowrap ${
                    activeTab === 'details'
                      ? 'bg-teal-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  お問い合わせ詳細
                </button>
                <button
                  onClick={() => setActiveTab('messages')}
                  className={`px-4 py-2 rounded-lg font-medium transition-colors whitespace-nowrap relative ${
                    activeTab === 'messages'
                      ? 'bg-teal-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  メッセージ ({contactMessages.length})
                  {contactUnreadCounts[selectedContact.id] > 0 && (
                    <span className="absolute -top-1 -right-1 flex h-5 w-5 items-center justify-center">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-orange-400 opacity-75"></span>
                      <span className="relative inline-flex rounded-full h-5 w-5 bg-orange-500 items-center justify-center text-white text-xs font-bold">
                        {contactUnreadCounts[selectedContact.id]}
                      </span>
                    </span>
                  )}
                </button>
              </div>

              {/* Details Tab */}
              {activeTab === 'details' && (
                <>
                  {/* Status Update */}
                  <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                    <label className="block text-sm font-medium text-gray-700 mb-2">ステータス更新</label>
                    <div className="grid grid-cols-2 gap-2">
                      <button
                        onClick={() => updateContactStatus(selectedContact.id, 'open')}
                        className={`px-4 py-2 rounded-lg font-medium transition-colors whitespace-nowrap ${
                          selectedContact.status === 'open'
                            ? 'bg-blue-600 text-white'
                            : 'bg-white border border-blue-600 text-blue-600 hover:bg-blue-50'
                        }`}
                      >
                        対応中
                      </button>
                      <button
                        onClick={() => updateContactStatus(selectedContact.id, 'closed')}
                        className={`px-4 py-2 rounded-lg font-medium transition-colors whitespace-nowrap ${
                          selectedContact.status === 'closed'
                            ? 'bg-green-600 text-white'
                            : 'bg-white border border-green-600 text-green-600 hover:bg-green-50'
                        }`}
                      >
                        完了
                      </button>
                    </div>
                  </div>

                  {/* Contact Information */}
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-sm text-gray-500">お問い合わせID</p>
                        <p className="text-sm font-medium text-gray-900 mt-1 break-all">{selectedContact.id}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">受信日時</p>
                        <p className="text-sm font-medium text-gray-900 mt-1">
                          {new Date(selectedContact.created_at).toLocaleString('ja-JP')}
                        </p>
                      </div>
                    </div>

                    <div className="border-t border-gray-200 pt-4">
                      <h3 className="text-lg font-semibold text-gray-900 mb-3">お客様情報</h3>
                      <div className="space-y-3">
                        <div>
                          <p className="text-sm text-gray-500">お名前</p>
                          <p className="text-sm font-medium text-gray-900 mt-1">{selectedContact.customer_name}</p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-500">メールアドレス</p>
                          <p className="text-sm font-medium text-gray-900 mt-1">{selectedContact.customer_email}</p>
                        </div>
                      </div>
                    </div>

                    <div className="border-t border-gray-200 pt-4">
                      <h3 className="text-lg font-semibold text-gray-900 mb-3">お問い合わせ内容</h3>
                      <div className="space-y-3">
                        <div>
                          <p className="text-sm text-gray-500">件名</p>
                          <p className="text-sm font-medium text-gray-900 mt-1">{selectedContact.subject}</p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-500">メッセージ</p>
                          <p className="text-sm font-medium text-gray-900 mt-1 whitespace-pre-wrap">{selectedContact.message}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </>
              )}

              {/* Messages Tab */}
              {activeTab === 'messages' && (
                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">顧客メッセージ</h3>
                  
                  {/* Messages Display */}
                  <div className="max-h-64 overflow-y-auto mb-4 space-y-3">
                    {contactMessages.length === 0 ? (
                      <div className="text-center py-8 text-gray-400">
                        <i className="ri-chat-3-line text-4xl mb-2"></i>
                        <p>このお問い合わせにはメッセージがありません</p>
                      </div>
                    ) : (
                      contactMessages.map((message) => (
                        <div
                          key={message.id}
                          className={`flex ${message.sender_type === 'admin' ? 'justify-end' : 'justify-start'}`}
                        >
                          <div className={`max-w-md px-4 py-3 rounded-lg relative ${
                            message.sender_type === 'admin'
                              ? 'bg-teal-600 text-white'
                              : message.is_read 
                                ? 'bg-white border border-gray-200 text-gray-900'
                                : 'bg-orange-50 border-2 border-orange-200 text-gray-900'
                          }`}>
                            {message.sender_type === 'user' && !message.is_read && (
                              <div className="absolute -top-2 -left-2">
                                <span className="flex h-4 w-4">
                                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-orange-400 opacity-75"></span>
                                  <span className="relative inline-flex rounded-full h-4 w-4 bg-orange-500"></span>
                                </span>
                              </div>
                            )}
                            <p className="text-sm">{message.message}</p>
                            <p className={`text-xs mt-2 ${
                              message.sender_type === 'admin'
                                ? 'text-teal-100'
                                : message.is_read
                                  ? 'text-gray-500'
                                  : 'text-orange-600 font-semibold'
                            }`}>
                              {message.sender_type === 'admin' ? '管理者' : message.is_read ? '顧客' : '顧客（新着）'} • {formatDate(message.created_at)}
                            </p>
                          </div>
                        </div>
                      ))
                    )}
                  </div>

                  {/* Send Message */}
                  <div className="flex space-x-3">
                    <input
                      type="text"
                      value={newAdminMessage}
                      onChange={(e) => setNewAdminMessage(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && sendAdminContactMessage()}
                      className="flex-1 bg-white border border-gray-300 text-gray-900 rounded-lg px-4 py-3 outline-none focus:ring-2 focus:ring-teal-500 text-sm"
                      placeholder="返信を入力..."
                    />
                    <button
                      onClick={sendAdminContactMessage}
                      disabled={!newAdminMessage.trim()}
                      className="bg-teal-600 hover:bg-teal-700 disabled:bg-gray-300 text-white px-4 py-3 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
                    >
                      <i className="ri-send-plane-fill"></i>
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Agent Modal */}
      {showAgentModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50" onClick={() => setShowAgentModal(false)}>
          <div className="bg-white rounded-lg max-w-md w-full" onClick={(e) => e.stopPropagation()}>
            <div className="border-b border-gray-200 px-6 py-4 flex items-center justify-between">
              <h2 className="text-xl font-bold text-gray-900">
                {selectedAgent ? '代理店を編集' : '代理店を追加'}
              </h2>
              <button
                onClick={() => setShowAgentModal(false)}
                className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 transition-colors"
              >
                <i className="ri-close-line text-2xl"></i>
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">代理店名</label>
                <input
                  type="text"
                  value={agentFormData.agent_name}
                  onChange={(e) => setAgentFormData({ ...agentFormData, agent_name: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                  placeholder="代理店名を入力"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">メールアドレス</label>
                <input
                  type="email"
                  value={agentFormData.agent_email}
                  onChange={(e) => setAgentFormData({ ...agentFormData, agent_email: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                  placeholder="メールアドレスを入力"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">パスワード</label>
                <input
                  type="text"
                  value={agentFormData.agent_password}
                  onChange={(e) => setAgentFormData({ ...agentFormData, agent_password: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                  placeholder="パスワードを入力"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">手数料（%）</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={agentFormData.fee_percentage}
                  onChange={(e) => setAgentFormData({ ...agentFormData, fee_percentage: parseFloat(e.target.value) || 0 })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                  placeholder="手数料を入力（例：5 または 1.25）"
                />
                <p className="mt-1 text-xs text-gray-500">例：5 または 1.25</p>
              </div>

              <div className="flex space-x-3 pt-4">
                <button
                  onClick={() => setShowAgentModal(false)}
                  className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-medium whitespace-nowrap"
                >
                  キャンセル
                </button>
                <button
                  onClick={selectedAgent ? handleUpdateAgent : handleAddAgent}
                  className="flex-1 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors font-medium whitespace-nowrap"
                >
                  {selectedAgent ? '更新' : '追加'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
